import { api } from 'api/api';
import { Endpoints } from 'api/constants';
import { TableDataResponse } from 'types/general';
import { IShopItem, IShopItemByBenefit } from 'types/income';
import { getInvertedKeys, toCents } from 'utils/general';
import { isDefined } from 'utils/isDefined';
import { ShopItemsByBenefitFilters, ShopItemsFilters } from './shop.types';

const getFilters = (filters: Partial<ShopItemsFilters>) => ({
  page: filters.page,
  // TODO: ask for BE to implement pageSize
  // pageSize: filters.pageSize,
  filter: {
    name: filters.name || undefined,
    priceFrom: isDefined(filters.price?.from)
      ? toCents(filters.price.from)
      : undefined,
    priceTo: isDefined(filters.price?.to)
      ? toCents(filters.price.to)
      : undefined,
    itemIds: filters.items,
    countries: filters.countries,
    cities: filters.cities,
    supplierIds: filters.suppliers?.value,
    benefitPlanTypes: filters.benefitCategories,
    categories: filters.categories,
    invertedFilters: getInvertedKeys([
      { supplierIds: filters.suppliers?.isInverted },
    ]),
    ...(filters.taxExemptionCountries?.length &&
    filters.taxExemptionStatus?.length
      ? {
          taxExemptionCountries: filters.taxExemptionCountries,
          taxExemptionStatus: filters.taxExemptionStatus,
        }
      : {}),
    sort: filters.sort
      ? `${filters.sort.order === 'desc' ? '-' : ''}${filters.sort.columnKey}`
      : undefined,
  },
});

interface GetShopItems {
  filters: Partial<ShopItemsFilters>;
}

interface GetShopItemsByBenefit {
  benefitId: string;
  filters: Partial<ShopItemsByBenefitFilters>;
}

interface AddToSelection {
  shopItemId: string;
}

interface RemoveFromSelection {
  shopItemId: string;
}

interface BulkAddToSelection {
  filters: Partial<ShopItemsFilters>;
}

interface BulkRemoveFromSelection {
  filters: Partial<ShopItemsFilters>;
}

export const loaders = {
  getShopItems: ({ filters }: GetShopItems) => {
    return api<TableDataResponse<IShopItem>>({
      url: filters.isSelected
        ? Endpoints.clientAdmin.shop.selected
        : Endpoints.clientAdmin.shop.root,
      version: 'v2',
      params: getFilters(filters),
    });
  },
  getShopItemsByBenefit: ({ benefitId, filters }: GetShopItemsByBenefit) => {
    return api<TableDataResponse<IShopItemByBenefit>>({
      url: filters.isSelected
        ? Endpoints.clientAdmin.shop.byBenefit.selected.replace(
            ':benefitId',
            benefitId,
          )
        : Endpoints.clientAdmin.shop.byBenefit.root.replace(
            ':benefitId',
            benefitId,
          ),
      version: 'v2',
      params: getFilters(filters),
    });
  },
  addToSelection: ({ shopItemId }: AddToSelection) => {
    return api({
      url: Endpoints.clientAdmin.shop.selection.byId.replace(
        ':shopItemId',
        shopItemId,
      ),
      method: 'post',
    });
  },
  removeFromSelection: ({ shopItemId }: RemoveFromSelection) => {
    return api({
      url: Endpoints.clientAdmin.shop.selection.byId.replace(
        ':shopItemId',
        shopItemId,
      ),
      method: 'delete',
    });
  },
  bulkAddToSelection: ({ filters }: BulkAddToSelection) => {
    return api({
      url: Endpoints.clientAdmin.shop.selection.bulk,
      method: 'post',
      params: {
        filter: getFilters(filters).filter,
      },
    });
  },
  bulkRemoveFromSelection: ({ filters }: BulkRemoveFromSelection) => {
    return api({
      url: Endpoints.clientAdmin.shop.selection.bulk,
      method: 'delete',
      params: {
        filter: getFilters(filters).filter,
      },
    });
  },
};
